import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { ApplicationType } from '@/contexts/dynamic-config';
import {
  CatalogIngredientListOptionComponent,
  ManualIngredientListOptionComponent,
} from './menu-item-search-ingredient-list-items';
import {
  convertToMenuItemIngredientFormState,
  IngredientActions,
  MenuItemIngredientFormState,
} from '@/components/menu/menu-item-ingredient-form';
import {
  createListSections,
  isManualIngredientViaMetadata,
} from './menu-item-search-helpers';
import { createStyles } from '@/contexts/theme-context';
import { FilterPills } from '@/components/filter-pills';
import { FONTS, SCREEN_PADDING } from '@/styles';
import {
  IngredientSearchResultMetadataType,
  MenuItem,
  useMenuInfoContext,
} from '@/contexts/menu-info-context';
import {
  IngredientType,
  ManualIngredientMetadata,
} from './menu-item-search-types';
import { itemListItemKeyExtractor } from '@/components/orders/orderable-item-list';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { ItemSearchInput } from '@/components/item-search-input';
import {
  KeyboardAwareRecyclerList,
  ListSection,
} from '@/components/recycler-list';
import { ListOption } from '@/components/list-item';
import { ManualIngredientListItem } from '@/components/menu/manual-ingredient-list-item';
import { MenuItemIngredientActionSheet } from '@/components/menu/menu-item-ingredient-action-sheet';
import { MICRO_CONVERSION, roundMicrosToNearestWholeCent } from '@/utils/money';
import { MultiOrderGuideButton } from '@/components/orders/multi-order-guide-button';
import { OrderGuidePartitionSectionActionSheet } from '@/components/order-guide/order-guide-partition-section-sheet';
import { PackUnit } from '@/enums';
import { PillButton, PillButtonType } from '@/components/pill-button';
import { Type } from '@/components/toast';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSearchMenuItemIngredients } from '@/hooks/use-search-menu-item-ingredients';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  menuItem: MenuItem;
}

enum FilterValues {
  CATALOG = 'CATALOG',
  CUSTOM = 'CUSTOM',
}

export const MenuItemSearchComponent: React.FC<Props> = ({ menuItem }) => {
  const { addMenuItemIngredient, removeMenuItemIngredient } =
    useMenuInfoContext();
  const { bottom } = useSafeAreaInsets();
  const { showToast } = useToasts();
  const [styles] = useStyles();
  const [
    partitionSelectorVariantPackUUID,
    setPartitionSelectorVariantPackUUID,
  ] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [selectSearchText, setSelectSearchText] = useState(false);
  const [showEditIngredientActionSheet, setShowEditIngredientActionSheet] =
    useState(false);
  const [targetIngredient, setTargetIngredient] =
    useState<MenuItemIngredientFormState>();
  const [ingredientMetadata, setIngredientMetadata] =
    useState<ItemListOptionMetadata>();
  const [filterValue, setFilterValue] = useState<FilterValues | null>(null);

  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;

  const menuItemUUID = menuItem.uuid;

  const {
    clearSearch,
    allIngredients,
    catalogIngredients,
    manualIngredients,
    queryLoading,
    searchIngredients,
  } = useSearchMenuItemIngredients();

  const renderOrderGuideButton = useCallback((variantPackUUID: string) => {
    return (
      <MultiOrderGuideButton
        openPartitionSelector={() => {
          setPartitionSelectorVariantPackUUID(variantPackUUID);
        }}
        variantPackUUID={variantPackUUID}
      />
    );
  }, []);

  const clearSearchInput = useCallback(() => {
    setInputText('');
    clearSearch();
  }, [clearSearch]);

  const showIngredientToast = useCallback(
    (action: 'add' | 'remove') =>
      showToast({
        autoCloseTimeout: 2000,
        content: i18n.t(
          action === 'add' ? 'added_to_menu_item' : 'removed_from_menu_item',
          {
            menuItem: menuItem.name,
          },
        ),
        type: Type.POSITIVE,
      }),
    [menuItem.name, showToast],
  );
  // This function is only for catalog ingredients as manual ingredients do not have a variant pack
  const addVariantPackAsIngredient = useCallback(
    (option: ItemListOptionMetadata) => {
      const ingredientData = {
        ...(typeof option.unitPriceInMicros === 'number'
          ? {
              variantPackCostInMicros: option.unitPriceInMicros,
            }
          : {}),
        variantPackUUID: option.variantPackUUID,
      };
      addMenuItemIngredient(ingredientData, menuItemUUID);
      showIngredientToast('add');
    },
    [addMenuItemIngredient, menuItemUUID, showIngredientToast],
  );

  const addMetadataAsIngredient = useCallback(
    (metadata: IngredientSearchResultMetadataType) => {
      const { baseUnit, displayName, unitCount, unitPriceInMicros, packUnit } =
        metadata;

      const roundedPriceInMicros = roundMicrosToNearestWholeCent(
        unitPriceInMicros!,
      );
      const ingredientData = {
        manualIngredient: {
          base_unit: baseUnit,
          cost_in_micros: roundedPriceInMicros,
          ingredient_name: displayName,
          pack_unit: packUnit ?? undefined,
          unit_count: unitCount,
        },
        variantPackCostInMicros: roundedPriceInMicros,
        variantPackUUID: isManualIngredientViaMetadata(metadata)
          ? undefined
          : (metadata as ItemListOptionMetadata).variantPackUUID,
      };
      const menuItemIngredientUUID = isManualIngredientViaMetadata(metadata)
        ? (metadata as ManualIngredientMetadata).menuItemIngredientUUID
        : undefined;

      addMenuItemIngredient(
        ingredientData,
        menuItemUUID,
        menuItemIngredientUUID,
      );
      showIngredientToast('add');
    },
    [addMenuItemIngredient, menuItemUUID, showIngredientToast],
  );

  const variantPackIngredientUUIDMap: Partial<Record<string, string>> =
    useMemo(() => {
      return menuItem.ingredients.reduce((acc, ing) => {
        return ing.variantPackUUID
          ? { ...acc, [ing.variantPackUUID]: ing.ingredientUUID }
          : acc;
      }, {});
    }, [menuItem.ingredients]);

  const handleIngredientSelection = useCallback(
    (
      ingredientUUID: string | undefined,
      metadata: IngredientSearchResultMetadataType,
    ) => {
      // Represents an ingredient that has already been used in this menu item
      const existingIngredient = menuItem.ingredients.find(
        (ing) => ing.ingredientUUID === ingredientUUID,
      );

      // Handle removing ingredient from this menu item
      if (existingIngredient) {
        removeMenuItemIngredient(
          existingIngredient.ingredientMetadataUUID,
          menuItemUUID,
        );
        showIngredientToast('remove');
      }
      // Handle adding manual ingredient to this menu item
      else if (isManualIngredientViaMetadata(metadata)) {
        metadata = metadata as ManualIngredientMetadata;
        addMetadataAsIngredient(metadata);
      }
      // Handle adding catalog ingredient to this menu item and show edit ingredient form if necessary
      else {
        metadata = metadata as ItemListOptionMetadata;

        if (
          metadata.packUnit === PackUnit.Case &&
          (!metadata.baseUnit || metadata.unitCount === 1)
        ) {
          setTargetIngredient(convertToMenuItemIngredientFormState(metadata));
          setIngredientMetadata(metadata);
          setShowEditIngredientActionSheet(true);
        }
        // Handle adding Variant Pack as ingredient to this menu item as an ingredient
        else {
          addVariantPackAsIngredient(metadata);
        }
      }
      setSelectSearchText(true);
    },
    [
      addMetadataAsIngredient,
      addVariantPackAsIngredient,
      menuItem.ingredients,
      menuItemUUID,
      removeMenuItemIngredient,
      showIngredientToast,
    ],
  );

  const handleFormSubmit = useCallback(
    (formState: MenuItemIngredientFormState) => {
      if (!ingredientMetadata) {
        return;
      }
      const ingredientData = {
        ...ingredientMetadata,
        baseUnit: formState.basePackUnit,
        displayName: formState.ingredientName,
        packUnit: formState.packUnit,
        unitCount:
          formState.packUnit === PackUnit.Case ? formState.baseUnitCount : 1,
        unitPriceInMicros: Number(formState.ingredientPrice) * MICRO_CONVERSION,
      };
      addMetadataAsIngredient(ingredientData);
      setShowEditIngredientActionSheet(false);
    },
    [ingredientMetadata, addMetadataAsIngredient],
  );

  const listData: ListSection<IngredientType>[] = useMemo(() => {
    const catalogResultsLabel = i18n.t('results_from_catalog');
    const manualResultsLabel = i18n.t('past_manual_entries');
    const previouslyAddedLabel = i18n.t('previously_added_ingredients');
    switch (filterValue) {
      case FilterValues.CATALOG:
        return createListSections({
          catalogIngredients: !inputText
            ? (allIngredients.catalog ?? [])
            : (catalogIngredients ?? []),
          catalogResultsLabel: !inputText
            ? previouslyAddedLabel
            : catalogResultsLabel,
        });
      case FilterValues.CUSTOM:
        return createListSections({
          manualIngredients: !inputText
            ? (allIngredients.manual ?? [])
            : (manualIngredients ?? []),
          manualResultsLabel: !inputText
            ? previouslyAddedLabel
            : manualResultsLabel,
        });
      default:
        return createListSections({
          catalogIngredients: !inputText
            ? (allIngredients.catalog ?? [])
            : (catalogIngredients ?? []),
          catalogResultsLabel: !inputText
            ? previouslyAddedLabel
            : catalogResultsLabel,
          manualIngredients: !inputText
            ? (allIngredients.manual ?? [])
            : (manualIngredients ?? []),
          manualResultsLabel,
        });
    }
  }, [
    filterValue,
    inputText,
    allIngredients.catalog,
    allIngredients.manual,
    catalogIngredients,
    manualIngredients,
  ]);

  const handleOnSearchItems = async (text: string) => {
    setInputText(text.trim());
    await searchIngredients(text);
  };

  const filterButtons: PillButtonType[] = useMemo(() => {
    return [
      {
        active: filterValue === FilterValues.CATALOG,
        label: i18n.t('menu_item_ingredient_search.filter.catalog'),
        onClear: () => setFilterValue(null),
        onPress: () =>
          setFilterValue(
            filterValue === FilterValues.CATALOG ? null : FilterValues.CATALOG,
          ),
      },
      {
        active: filterValue === FilterValues.CUSTOM,
        label: i18n.t('menu_item_ingredient_search.filter.custom'),
        onClear: () => setFilterValue(null),
        onPress: () =>
          setFilterValue(
            filterValue === FilterValues.CUSTOM ? null : FilterValues.CUSTOM,
          ),
      },
    ];
  }, [filterValue]);

  const renderPillButton = useCallback(
    (button: PillButtonType) => {
      enum FilterTypeKeys {
        Catalog = 'menu_item_ingredient_search.filter.catalog',
        Custom = 'menu_item_ingredient_search.filter.custom',
      }
      button.collapsed =
        (button.label === i18n.t(FilterTypeKeys.Catalog) &&
          filterValue === FilterValues.CUSTOM) ||
        (button.label === i18n.t(FilterTypeKeys.Custom) &&
          filterValue === FilterValues.CATALOG);

      return <PillButton button={button} key={button.label} />;
    },
    [filterValue],
  );

  return (
    <View
      style={[
        styles.listContainer,
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
    >
      <View collapsable={false}>
        <ItemSearchInput
          clearSearch={clearSearchInput}
          containerStyle={styles.searchContainer}
          onSearchItems={handleOnSearchItems}
          placeholder={i18n.t('add_ingredient')}
          resetSelectAllText={() => setSelectSearchText(false)}
          selectAllText={selectSearchText}
        />
        <View style={styles.filterPillsContainer}>
          <FilterPills
            buttons={filterButtons}
            renderPillButton={renderPillButton}
          />
        </View>
      </View>
      {queryLoading ? (
        <ActivityIndicator />
      ) : (
        <KeyboardAwareRecyclerList<IngredientType>
          estimatedItemSize={160}
          extraSpacing={40}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          keyExtractor={(item) =>
            isManualIngredientViaMetadata(item.metadata!)
              ? [
                  item.title,
                  item.value,
                  (item.metadata! as ManualIngredientMetadata)
                    .menuItemIngredientUUID,
                ].join('-')
              : itemListItemKeyExtractor(item as ItemListOption)
          }
          ListHeaderComponent={
            inputText ? (
              <ManualIngredientListItem
                initialName={inputText}
                menuItemUUID={menuItemUUID}
                onSubmit={showIngredientToast}
              />
            ) : null
          }
          listItemProps={{
            listItemStyle: [styles.listItemNoPaddingHorizontal],
            renderMainContent: (option) => {
              const { metadata } = option;
              if (!metadata) {
                return null;
              } else if (isManualIngredientViaMetadata(metadata)) {
                return (
                  <ManualIngredientListOptionComponent
                    handleIngredientSelection={handleIngredientSelection}
                    item={option as ListOption<ManualIngredientMetadata>}
                    menuItem={menuItem}
                  />
                );
              } else {
                return (
                  <CatalogIngredientListOptionComponent
                    handleIngredientSelection={handleIngredientSelection}
                    item={option as ItemListOption}
                    renderOrderGuideButton={
                      !isCatalogLimited ? renderOrderGuideButton : undefined
                    }
                    variantPackIngredientUUIDMap={variantPackIngredientUUIDMap}
                  />
                );
              }
            },
          }}
          listItemSeparatorProps={{
            style: styles.listItemSeparatorMarginHorizontal,
          }}
          renderSectionHeader={({ title }) => {
            if (filterValue === FilterValues.CUSTOM) {
              return (
                <Text
                  style={[
                    styles.separatorText,
                    styles.sectionHeaderMarginBottom,
                  ]}
                >
                  {i18n.t('past_manual_entries')}
                </Text>
              );
            } else if (filterValue === FilterValues.CATALOG) {
              return (
                <Text style={styles.separatorText}>
                  {i18n.t('previously_added_ingredients')}
                </Text>
              );
            } else {
              return (
                <Text
                  style={[
                    styles.separatorText,
                    title === i18n.t('past_manual_entries')
                      ? styles.sectionHeaderMarginBottom
                      : undefined,
                  ]}
                >
                  {title}
                </Text>
              );
            }
          }}
          sections={listData}
          showSeparatorBetweenSections
          stickyHeaderIndices={[]}
        />
      )}
      {partitionSelectorVariantPackUUID ? (
        <OrderGuidePartitionSectionActionSheet
          onClose={() => setPartitionSelectorVariantPackUUID(null)}
          variantPackUUID={partitionSelectorVariantPackUUID}
        />
      ) : null}
      {showEditIngredientActionSheet &&
      targetIngredient &&
      ingredientMetadata ? (
        <MenuItemIngredientActionSheet
          action={IngredientActions.ADD}
          ingredient={targetIngredient}
          onSubmit={handleFormSubmit}
          originalBaseUnit={ingredientMetadata.baseUnit || undefined}
          setShowIngredientForm={setShowEditIngredientActionSheet}
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  filterPillsContainer: {
    marginTop: 8,
  },
  listContainer: {
    flex: 1,
  },
  listItemNoPaddingHorizontal: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  listItemSeparatorMarginHorizontal: {
    marginHorizontal: 16,
  },
  searchContainer: {
    backgroundColor: theme.colors.background,
    margin: 16,
    marginBottom: 0,
  },
  sectionHeaderMarginBottom: {
    marginBottom: 16,
  },
  separatorText: {
    ...FONTS.primarySemiBold,
    backgroundColor: theme.colors.white,
    color: theme.colors.inactiveText,
    fontSize: 16,
    lineHeight: 18,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
}));
